import { useMutation, useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/lib/constants';
import {
    checkAuthTokenUpgrade,
    completeAffiliateRegistration,
    getAffiliateUser,
    loginWithPassword,
    register,
    sendResetPasswordEmail,
    tokenUpdatePassword,
    transferAffiliateAccount,
} from '@/lib/api/auth/api';
import type { AffiliateCompleteRegistrationPayload, TransferAffiliateAccountPayload } from '@/lib/api/auth/types';

export function useLoginMutation() {
    return useMutation({
        mutationKey: [QUERY_KEYS.PASSWORD_LOGIN],
        mutationFn: ({ email_or_username, password }: { email_or_username: string; password: string }) =>
            loginWithPassword(email_or_username, password),
        meta: { error: { sentryMessage: '[LOGIN] failed-to-fetch-login' } },
    });
}

export function useAuthTokenCheckUpgradeMutation() {
    return useMutation({
        mutationKey: [QUERY_KEYS.AUTH_TOKEN_CHECK_UPGRADE],
        mutationFn: ({ authToken }: { authToken: string }) => checkAuthTokenUpgrade(authToken),
        meta: { error: { sentryMessage: '[AUTH_TOKEN_CHECK_UPGRADE] failed-to-check-or-upgrade-auth-token' } },
    });
}

export function useResetPasswordMutation() {
    return useMutation({
        mutationKey: [QUERY_KEYS.PASSWORD_RESET],
        mutationFn: ({
            token,
            password,
            confirmPassword,
        }: {
            token: string;
            password: string;
            confirmPassword: string;
        }) => tokenUpdatePassword(token, password, confirmPassword),
        meta: { error: { sentryMessage: '[RESET_PASSWORD] failed-to-reset-password' } },
    });
}

export function useForgotPasswordMutation() {
    return useMutation({
        mutationKey: [QUERY_KEYS.PASSWORD_FORGOT],
        mutationFn: ({ email }: { email: string }) => sendResetPasswordEmail(email),
        meta: { error: { sentryMessage: '[FORGOT_PASSWORD] failed-to-send-forgot-password-email' } },
    });
}

export function useRegisterMutation() {
    return useMutation({
        mutationKey: [QUERY_KEYS.REGISTER],
        mutationFn: register,
        meta: { error: { sentryMessage: '[REGISTER] failed-to-register-user' } },
    });
}

// Affiliate Registration Queries & Mutations

/**
 * Query to fetch affiliate user data from the registration token.
 * Uses useQuery directly (not useStanQuery) since this is a pre-auth endpoint
 * that needs to work before authentication.
 */
export function useAffiliateUserQuery(token: string | null) {
    return useQuery({
        queryKey: [QUERY_KEYS.AFFILIATE_USER, token],
        queryFn: () => getAffiliateUser(token!),
        enabled: !!token,
        retry: false,
        staleTime: 1000 * 60 * 60, // 1 hour - token data doesn't change
        gcTime: 1000 * 60 * 60, // 1 hour
    });
}

/**
 * Mutation to complete affiliate registration (create new account)
 */
export function useCompleteAffiliateRegistrationMutation() {
    return useMutation({
        mutationKey: [QUERY_KEYS.AFFILIATE_COMPLETE_REGISTRATION],
        mutationFn: ({ payload, token }: { payload: AffiliateCompleteRegistrationPayload; token: string }) =>
            completeAffiliateRegistration(payload, token),
        meta: { error: { sentryMessage: '[AFFILIATE] failed-to-complete-affiliate-registration' } },
    });
}

/**
 * Mutation to transfer affiliate account to existing Stan user
 */
export function useTransferAffiliateAccountMutation() {
    return useMutation({
        mutationKey: [QUERY_KEYS.AFFILIATE_TRANSFER_ACCOUNT],
        mutationFn: ({ payload, token }: { payload: TransferAffiliateAccountPayload; token: string }) =>
            transferAffiliateAccount(payload, token),
        meta: { error: { sentryMessage: '[AFFILIATE] failed-to-transfer-affiliate-account' } },
    });
}
